import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export const stopAddress = (s) => [s.address, s.city].filter(Boolean).join(", ") || s.name;

const pin = (n, active, color) => L.divIcon({
  className: "",
  html: `<div style="width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font:600 12px 'JetBrains Mono',monospace;color:#fff;background:${color || (active ? "#f59e0b" : "#3b82f6")};border:2px solid #0b1220;box-shadow:0 2px 6px rgba(0,0,0,.5)">${n}</div>`,
  iconSize: [26, 26], iconAnchor: [13, 13],
});

export default function RouteMap({ stops, current, onPick, picked }) {
  const box = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const fitted = useRef("");
  const [coords, setCoords] = useState({});
  const [loading, setLoading] = useState(0);

  useEffect(() => {
    map.current = L.map(box.current, { center: [31.5, -97.5], zoom: 7 });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "&copy; OpenStreetMap contributors" }).addTo(map.current);
    layer.current = L.layerGroup().addTo(map.current);
    return () => map.current.remove();
  }, []);

  useEffect(() => {
    let stop = false;
    (async () => {
      const todo = [...new Set(stops.map(stopAddress))].filter((a) => !(a in coords));
      setLoading(todo.length);
      for (const a of todo) {
        if (stop) return;
        const r = await api.get("/route-setup/geocode", { params: { address: a } }).then((x) => x.data).catch(() => ({ lat: null }));
        setCoords((p) => ({ ...p, [a]: r }));
        setLoading((n) => n - 1);
      }
    })();
    return () => { stop = true; };
  }, [stops]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    layer.current.clearLayers();
    const pts = [];
    stops.forEach((s, i) => {
      const c = coords[stopAddress(s)];
      if (!c?.lat) return;
      pts.push([c.lat, c.lon]);
      const pos = picked ? picked.indexOf(i) : -1;
      const icon = picked ? pin(pos >= 0 ? pos + 1 : "·", false, pos >= 0 ? "#10b981" : "#64748b") : pin(i + 1, i === current);
      L.marker([c.lat, c.lon], { icon }).on("click", () => onPick(i))
        .bindTooltip(`${i + 1}. ${s.name}${c.approx ? " (approx.)" : ""}`).addTo(layer.current);
    });
    if (pts.length > 1 && !picked) L.polyline(pts, { color: "#3b82f6", weight: 2, opacity: 0.6, dashArray: "6 6" }).addTo(layer.current);
    const key = pts.map((p) => p.join()).sort().join("|");
    if (pts.length && key !== fitted.current) {
      fitted.current = key;
      map.current.stop();
      map.current.fitBounds(pts, { padding: [30, 30], maxZoom: 13, animate: false });
    }
  }, [coords, stops, current, picked]); // eslint-disable-line react-hooks/exhaustive-deps

  const missing = stops.filter((s) => coords[stopAddress(s)] && !coords[stopAddress(s)].lat).length;
  return (
    <div className="relative">
      <div ref={box} className="h-[420px] w-full rounded-md z-0" data-testid="route-map" />
      {(loading > 0 || missing > 0) && (
        <div className="absolute top-2 right-2 z-[500] bg-card/90 backdrop-blur border rounded-md px-3 py-1.5 text-xs flex items-center gap-2" data-testid="route-map-status">
          {loading > 0 && <><Loader2 size={12} className="animate-spin" /> Finding {loading} address{loading === 1 ? "" : "es"}…</>}
          {loading === 0 && missing > 0 && <>{missing} address{missing === 1 ? "" : "es"} couldn't be placed on the map</>}
        </div>
      )}
    </div>
  );
}
